import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { datingDraft } from '../utils/dating';

const copy = {
  RU: { label: 'Год выпуска', range: 'Период', exact: 'Точный год', list: 'Несколько лет', year: 'Год', start: 'От', end: 'До', years: 'Годы через запятую', empty: 'Не ограничено', clear: 'Очистить', hint: 'Границы включаются. Пустое поле — без ограничения.', lower: 'Начало периода', upper: 'Конец периода' },
  EN: { label: 'Year of manufacture', range: 'Period', exact: 'Exact year', list: 'Multiple years', year: 'Year', start: 'From', end: 'To', years: 'Comma-separated years', empty: 'No limit', clear: 'Clear', hint: 'Bounds are inclusive. Leave a field empty for no limit.', lower: 'Period start', upper: 'Period end' },
};
const MIN = 1895, MAX = 1950;
const clamp = year => Math.max(MIN, Math.min(MAX, Number(year)));

export default function YearFields({ value, onChange, search = false }) {
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const mode = ['unknown', 'exact'].includes(value.mode) ? 'exact' : value.mode === 'list' ? 'list' : 'range';
  const start = value.yearFrom === '' ? MIN : clamp(value.yearFrom);
  const end = value.yearTo === '' ? MAX : clamp(value.yearTo);
  const update = (key, next) => onChange({ ...value, mode, [key]: next });
  const move = (key, next) => {
    const bound = key === 'yearFrom' ? value.yearTo : value.yearFrom;
    const limited = bound !== '' && bound != null
      ? (key === 'yearFrom' ? Math.min(next, Number(bound)) : Math.max(next, Number(bound))) : next;
    update(key, String(limited));
  };
  const clear = (key, label) => value[key] !== '' && value[key] != null && <button type="button" className="year-fields__clear" aria-label={`${labels.clear}: ${label}`} title={`${labels.clear}: ${label}`} onClick={() => update(key, '')}><X size={14} aria-hidden="true" /></button>;
  const field = (key, label, listed = false) => <label className="year-fields__field"><span>{label}</span><span className="year-fields__control"><input type="text" inputMode={listed ? 'text' : 'numeric'} placeholder={listed ? '1914, 1915, 1916' : mode === 'range' ? labels.empty : '1915'} value={value[key] ?? ''} onChange={event => update(key, event.target.value)} />{clear(key, label)}</span></label>;
  return <fieldset className="year-fields">
    <legend>{labels.label}</legend>
    <div className="year-fields__modes">
      {['exact', 'range', 'list'].map(item => <button type="button" key={item} aria-pressed={mode === item} onClick={() => onChange({ ...value, mode: item })}>{labels[item]}</button>)}
    </div>
    <div className="year-fields__inputs">
      {mode === 'exact' && field('year', labels.year)}
      {mode === 'range' && <>{field('yearFrom', labels.start)}{field('yearTo', labels.end)}</>}
      {mode === 'list' && field('years', labels.years, true)}
    </div>
    {mode === 'range' && <>
      <div className={`year-fields__slider${Math.abs(end-start) <= 2 ? ' is-close' : ''}`} style={{'--start': `${(start-MIN)/(MAX-MIN)*100}%`, '--end': `${(end-MIN)/(MAX-MIN)*100}%`}}
        onPointerDown={event => {
          if (event.target.tagName === 'INPUT') return;
          const box = event.currentTarget.getBoundingClientRect();
          const next = clamp(Math.round(MIN + (event.clientX-box.left)/box.width*(MAX-MIN)));
          move(Math.abs(next-start) < Math.abs(next-end) ? 'yearFrom' : 'yearTo', next);
        }}>
        <div className="year-fields__track" />
        <input type="range" min={MIN} max={MAX} step="1" value={start} aria-label={labels.lower} aria-valuetext={value.yearFrom || labels.empty} onChange={event => move('yearFrom', Number(event.target.value))} />
        <input type="range" min={MIN} max={MAX} step="1" value={end} aria-label={labels.upper} aria-valuetext={value.yearTo || labels.empty} onChange={event => move('yearTo', Number(event.target.value))} />
      </div>
      <div className="year-fields__ticks" aria-hidden="true"><span>1895</span><span>1920</span><span>1950</span></div>
      <small>{labels.hint}</small>
    </>}
  </fieldset>;
}

export function YearFilter({ filters, initialFilters = filters, onChange }) {
  const [draft, setDraft] = useState(() => datingDraft(initialFilters));
  const dateKey = JSON.stringify([filters.year ?? '', filters.yearFrom ?? '', filters.yearTo ?? '', filters.years ?? '']);
  const previousDateKey = useRef(dateKey);
  useEffect(() => {
    if (previousDateKey.current === dateKey) return;
    previousDateKey.current = dateKey;
    const [year, yearFrom, yearTo, years] = JSON.parse(dateKey);
    setDraft(current => ({ ...current, year, yearFrom, yearTo, years }));
  }, [dateKey]);
  return <YearFields search value={draft} onChange={next => {
    setDraft(next);
    const result = { ...filters };
    for (const key of ['year', 'yearFrom', 'yearTo', 'years']) delete result[key];
    if (next.mode === 'exact') result.year = next.year;
    if (['range', 'from'].includes(next.mode)) result.yearFrom = next.yearFrom;
    if (['range', 'to'].includes(next.mode)) result.yearTo = next.yearTo;
    if (next.mode === 'list') result.years = next.years.trim().replace(/[,;\s]+/g, ',');
    // This update contains only the active tab; keep inactive drafts locally.
    previousDateKey.current = JSON.stringify([result.year ?? '', result.yearFrom ?? '', result.yearTo ?? '', result.years ?? '']);
    onChange(result);
  }} />;
}
